/**
 * The paid plans, as one typed config. The landing page renders pricing only
 * from this file, so a price or a limit is a one-line change here.
 *
 * Prices are Petr's decision (Oct 2026) and final: the seller is not a VAT
 * payer, so there is no VAT on top. Plans are live (`draft: false`, 23. 10.
 * 2026): Stripe Checkout sells them (docs/billing/STRIPE_INTEGRATION.md).
 * Access is still granted from the access request. The limits below are
 * enforced on the server behind a kill switch (billing_config.enforce) that
 * stays off until a live test purchase works (see `LIMITS_ENFORCED`).
 */
import type { LandingLocale, Localized } from "@/lib/landing/locale";

export type PlanId = "start" | "firma";

/**
 * Everything a plan can include. `evidence` points at the code that makes the
 * feature real today, so the page never sells something the app does not do
 * (a unit test checks the files exist). `service` marks promises that are not
 * code, like support response times.
 */
export type FeatureId =
  | "fioSync"
  | "statementImport"
  | "autoMatching"
  | "aiExtraction"
  | "emailInbox"
  | "monthOverview"
  | "accountantBundle"
  | "invoicing"
  | "budgetAnalytics"
  | "privateMode"
  | "teamMembers"
  | "largerTeam"
  | "multiCompany"
  | "mcpConnector"
  | "fakturoidImport"
  | "prioritySupport";

export interface PlanFeature {
  label: Localized;
  /** A few words for the pricing card's checklist; `label` is the full line. */
  short: Localized;
  /** Repo-relative paths of the code behind the feature; empty only for a service. */
  evidence: readonly string[];
  service?: boolean;
}

export const FEATURES: Record<FeatureId, PlanFeature> = {
  fioSync: {
    short: { cs: "Stahování plateb z Fio každý den", en: "Daily Fio payment sync" },
    label: {
      cs: "Automatické stahování plateb z Fio každý den",
      en: "Daily automatic download of your Fio banka payments",
    },
    evidence: [
      "supabase/functions/fetch-fio-transactions/index.ts",
      "supabase/functions/daily-sync/index.ts",
    ],
  },
  statementImport: {
    short: { cs: "Import výpisů GPC/ABO a CSV", en: "GPC/ABO and CSV statement import" },
    label: {
      cs: "Nahrání výpisů GPC/ABO a CSV pro starší období",
      en: "GPC/ABO and CSV statement upload for older periods",
    },
    evidence: ["src/lib/gpc/gpcParser.ts", "src/components/transactions/GpcImportCard.tsx"],
  },
  autoMatching: {
    short: { cs: "Automatické párování s doklady", en: "Automatic matching with documents" },
    label: {
      cs: "Automatické párování plateb s doklady",
      en: "Automatic matching of payments and documents",
    },
    evidence: ["supabase/functions/run-ai-matching/index.ts"],
  },
  aiExtraction: {
    short: { cs: "AI přečte účtenky i faktury", en: "AI reads receipts and invoices" },
    label: {
      cs: "AI přečte účtenky a faktury z PDF i fotek",
      en: "AI reads receipts and invoices from PDFs and photos",
    },
    evidence: ["supabase/functions/extract-invoice/index.ts"],
  },
  emailInbox: {
    short: {
      cs: "E-mailová adresa firmy pro doklady",
      en: "A company e-mail address for documents",
    },
    label: {
      cs: "Vlastní e-mailová adresa vaší firmy pro přeposílání dokladů",
      en: "Your company's own e-mail address to forward documents to",
    },
    evidence: [
      "supabase/functions/process-inbox-email/index.ts",
      "src/components/settings/EmailInboxSection.tsx",
    ],
  },
  monthOverview: {
    short: {
      cs: "Přehled, co je spárované a co chybí",
      en: "See what is matched and what is missing",
    },
    label: {
      cs: "Přehled po měsících: co je spárované a co chybí",
      en: "Month-by-month view of what is matched and what is missing",
    },
    evidence: ["src/components/dashboard/StatusStrip.tsx", "src/lib/dashboard/txStatus.ts"],
  },
  accountantBundle: {
    short: {
      cs: "Podklady pro účetní: PDF, CSV, XLSX, ISDOC, POHODA (XML i přímé odeslání)",
      en: "Files for your accountant: PDF, CSV, XLSX, ISDOC, POHODA (XML and direct send)",
    },
    label: {
      cs: "Podklady pro účetní: PDF balíček dokladů za měsíc, exporty CSV, XLSX, ISDOC a POHODA (XML i přímé odeslání)",
      en: "Files for your accountant: monthly PDF bundle, CSV, XLSX, ISDOC and POHODA (XML and direct send) exports",
    },
    evidence: [
      "src/components/receipts/ReceiptsBundleButton.tsx",
      "src/components/invoices/ExportSection.tsx",
      "src/components/transactions/PohodaExportCard.tsx",
    ],
  },
  invoicing: {
    short: { cs: "Fakturace s QR platbou a ARES", en: "Invoicing with QR payments and ARES" },
    label: {
      cs: "Vystavování faktur s QR platbou, šablonami a načtením firmy z ARES",
      en: "Invoicing with QR payment codes, templates and ARES company lookup",
    },
    evidence: [
      "src/pages/InvoiceCreate.tsx",
      "src/lib/invoices/paymentQR.ts",
      "supabase/functions/ares-lookup/index.ts",
    ],
  },
  budgetAnalytics: {
    short: { cs: "Rozpočet a analytika", en: "Budget and analytics" },
    label: {
      cs: "Rozpočet pravidelných plateb a analytika příjmů a výdajů",
      en: "Budget of recurring payments and income/expense analytics",
    },
    evidence: ["src/pages/BusinessBudgetTracker.tsx", "src/pages/Analytics.tsx"],
  },
  privateMode: {
    short: { cs: "Soukromý režim pro osobní rozpočet", en: "Private mode for a personal budget" },
    label: {
      cs: "Soukromý režim: osobní rozpočet pravidelných výdajů, oddělený od firmy",
      en: "Private mode: a personal budget of recurring expenses, kept apart from the company",
    },
    evidence: ["src/hooks/useMode.tsx", "src/pages/BudgetTracker.tsx"],
  },
  teamMembers: {
    short: {
      cs: "Přístup pro účetní nebo kolegu",
      en: "Access for your accountant or a colleague",
    },
    label: {
      cs: "Přizvání účetní nebo kolegy do firmy",
      en: "Add your accountant or a colleague to the company",
    },
    evidence: [
      "src/components/settings/OrganizationMembersSection.tsx",
      "src/pages/InviteAccept.tsx",
    ],
  },
  largerTeam: {
    short: { cs: "Tým s rolemi člen a správce", en: "A team with member and admin roles" },
    label: {
      cs: "Víc členů týmu s rolemi (člen, správce)",
      en: "More team members with roles (member, admin)",
    },
    evidence: [
      "src/components/settings/OrganizationMembersSection.tsx",
      "src/pages/InviteAccept.tsx",
    ],
  },
  multiCompany: {
    short: { cs: "Více firem pod jedním přihlášením", en: "Several companies, one login" },
    label: {
      cs: "Více firem pod jedním přihlášením",
      en: "Several companies under one login",
    },
    evidence: ["src/components/settings/OrganizationMembersSection.tsx"],
  },
  mcpConnector: {
    short: { cs: "AI asistenti přes MCP, např. Claude", en: "AI assistants via MCP, e.g. Claude" },
    label: {
      cs: "Napojení AI asistentů přes MCP (např. Claude)",
      en: "Connect AI assistants via MCP (e.g. Claude)",
    },
    evidence: [
      "supabase/functions/mcp/index.ts",
      "src/components/settings/McpConnectionsSection.tsx",
    ],
  },
  fakturoidImport: {
    short: { cs: "Převod z Fakturoidu", en: "Move over from Fakturoid" },
    label: {
      cs: "Převod faktur a klientů z Fakturoidu",
      en: "Move invoices and clients over from Fakturoid",
    },
    evidence: ["src/components/fakturoid/FakturoidImport.tsx"],
  },
  prioritySupport: {
    short: { cs: "Přednostní podpora", en: "Priority support" },
    label: {
      cs: "Přednostní podpora",
      en: "Priority support",
    },
    evidence: [],
    service: true,
  },
};

export interface PlanLimits {
  /** Organizations (companies) the subscriber can own. */
  companies: number;
  /** Fio accounts in total; CZK and EUR are separate accounts with separate tokens. */
  fioAccounts: number;
  /** People with access, the owner included (e.g. you and your accountant). */
  users: number;
  /** Documents the AI reads per month (receipts, invoices). */
  aiDocumentsPerMonth: number;
}

export interface Plan {
  id: PlanId;
  name: string;
  /** Not purchasable yet (no billing): SEO emits no JSON-LD Offer while true. */
  draft: boolean;
  /** CZK per month, the only billing period; final, no VAT (see `vatPayer`). */
  priceMonthlyCzk: number;
  /** Free trial length in days once billing ships; null = no trial is promised. */
  trialDays: number | null;
  /** Who it is for, in one line. */
  audience: Localized;
  limits: PlanLimits;
  /** Shown as "Everything in <plan>, plus:" above `features`. */
  includes?: PlanId;
  features: readonly FeatureId[];
  /**
   * The 5–6 checks on the pricing card: for a plan that `includes` another,
   * only what it adds. The rest of `allFeatures` is in the comparison table.
   */
  highlights: readonly FeatureId[];
  /** The plan the page highlights. */
  recommended?: boolean;
}

/**
 * Limits enforced in code: on the server, by billing_check() and its guards
 * (supabase/migrations/20261023130000_plan_enforcement.sql), mirrored from
 * this file. They bite only once billing_config.enforce is switched on at
 * go-live; until then every check passes.
 */
export const LIMITS_ENFORCED: Record<keyof PlanLimits, boolean> = {
  companies: true,
  fioAccounts: true,
  users: true,
  aiDocumentsPerMonth: true,
};

export const PLANS: readonly Plan[] = [
  {
    id: "start",
    name: "Start",
    draft: false,
    priceMonthlyCzk: 150,
    trialDays: 14,
    audience: {
      cs: "Pro OSVČ a s.r.o. s jedním majitelem.",
      en: "For sole traders and one-owner companies.",
    },
    limits: { companies: 1, fioAccounts: 2, users: 2, aiDocumentsPerMonth: 300 },
    features: [
      "fioSync",
      "statementImport",
      "autoMatching",
      "aiExtraction",
      "emailInbox",
      "monthOverview",
      "accountantBundle",
      "teamMembers",
    ],
    highlights: [
      "fioSync",
      "autoMatching",
      "aiExtraction",
      "emailInbox",
      "statementImport",
      "accountantBundle",
    ],
  },
  {
    id: "firma",
    name: "Firma",
    draft: false,
    priceMonthlyCzk: 350,
    trialDays: 14,
    audience: {
      cs: "Pro s.r.o. s týmem, fakturací a více firmami.",
      en: "For companies with a team, invoicing and several firms.",
    },
    limits: { companies: 3, fioAccounts: 10, users: 6, aiDocumentsPerMonth: 1500 },
    includes: "start",
    features: [
      "invoicing",
      "budgetAnalytics",
      "privateMode",
      "multiCompany",
      "largerTeam",
      "mcpConnector",
      "fakturoidImport",
      "prioritySupport",
    ],
    highlights: [
      "invoicing",
      "budgetAnalytics",
      "mcpConnector",
      "multiCompany",
      "largerTeam",
      "privateMode",
    ],
    recommended: true,
  },
];

/**
 * IDEx Corporate s.r.o. is not a VAT payer (neplátce DPH): the prices above
 * are final, and the page says so instead of showing any "bez/s DPH".
 */
export const vatPayer = false;

export function getPlan(id: PlanId): Plan {
  const plan = PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`Unknown plan: ${id}`);
  return plan;
}

/** Every feature of a plan, inherited ones first. */
export function allFeatures(plan: Plan): FeatureId[] {
  const inherited = plan.includes ? allFeatures(getPlan(plan.includes)) : [];
  return [...inherited, ...plan.features.filter((f) => !inherited.includes(f))];
}

/** Where a plan's button leads: the sign-up on /auth, which remembers the plan for the billing page. */
export function planSignupPath(plan: Plan): string {
  return `/auth?tab=register&plan=${plan.id}`;
}

/** "290 Kč" with Czech grouping ("7 900 Kč"), without Intl so server and browser agree. */
export function formatCzk(amount: number): string {
  return `${grouped(Math.round(amount))}\u00a0Kč`;
}

/** Czech plural: 1 účet, 2–4 účty, 5+ účtů. */
export function czPlural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}

function grouped(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0");
}

/** The plan's limits as short lines in one language ("2 účty Fio", "300 dokladů měsíčně"). */
export function limitLines(plan: Plan, locale: LandingLocale): string[] {
  const { companies, fioAccounts, users, aiDocumentsPerMonth } = plan.limits;
  const docs = grouped(aiDocumentsPerMonth);
  if (locale === "cs") {
    return [
      `${companies} ${czPlural(companies, "firma", "firmy", "firem")}`,
      `${fioAccounts} ${czPlural(fioAccounts, "účet", "účty", "účtů")} u Fio banky`,
      `${users} ${czPlural(users, "uživatel", "uživatelé", "uživatelů")}`,
      `${docs} ${czPlural(aiDocumentsPerMonth, "doklad", "doklady", "dokladů")} pro AI měsíčně`,
    ];
  }
  if (locale === "en") {
    return [
      `${companies} ${companies === 1 ? "company" : "companies"}`,
      `${fioAccounts} Fio ${fioAccounts === 1 ? "account" : "accounts"}`,
      `${users} ${users === 1 ? "user" : "users"}`,
      `${docs} AI-read ${aiDocumentsPerMonth === 1 ? "document" : "documents"} a month`,
    ];
  }
  return [
    `${companies} ${companies === 1 ? "Firma" : "Firmen"}`,
    `${fioAccounts} Fio-${fioAccounts === 1 ? "Konto" : "Konten"}`,
    `${users} Nutzer`,
    `${docs} ${aiDocumentsPerMonth === 1 ? "Beleg" : "Belege"} pro Monat mit KI-Erkennung`,
  ];
}

/** The plan's limits as short chips for the card ("2 účty Fio", "300 dokladů/měs."). */
export function limitChips(plan: Plan, locale: LandingLocale): string[] {
  const { companies, fioAccounts, users, aiDocumentsPerMonth } = plan.limits;
  const docs = grouped(aiDocumentsPerMonth);
  if (locale === "cs") {
    return [
      `${companies} ${czPlural(companies, "firma", "firmy", "firem")}`,
      `${fioAccounts} ${czPlural(fioAccounts, "účet", "účty", "účtů")} Fio`,
      `${users} ${czPlural(users, "uživatel", "uživatelé", "uživatelů")}`,
      `${docs} ${czPlural(aiDocumentsPerMonth, "doklad", "doklady", "dokladů")}/měs.`,
    ];
  }
  return [
    `${companies} ${companies === 1 ? "company" : "companies"}`,
    `${fioAccounts} Fio ${fioAccounts === 1 ? "account" : "accounts"}`,
    `${users} ${users === 1 ? "user" : "users"}`,
    `${docs} ${aiDocumentsPerMonth === 1 ? "document" : "documents"}/mo`,
  ];
}
